import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ButtonSpinner } from '../components/ui/Loader';
import { useLoginMutation } from '../store/api/authApi';
import { storeAuthData } from '../utils/tokenManager';

const COLORS = {
  bg: '#0E4A4A',
  aqua: '#A8E0E0',
  peach: '#F5C9A1',
  white: '#FFFFFF',
  border: 'rgba(168, 224, 224, 0.2)',
  borderFocus: 'rgba(168, 224, 224, 0.6)',
  error: '#FF6B6B',
};

const s = {
  page: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: COLORS.bg,
  },
  inputGroup: { marginBottom: 20 },
  label: {
    display: 'block', color: COLORS.aqua, fontSize: 13, fontWeight: 500,
    fontFamily: 'Tajawal, sans-serif', marginBottom: 8, letterSpacing: '0.5px', opacity: 0.9,
  },
  input: (hasError: boolean) => ({
    width: '100%', padding: '13px 16px', borderRadius: 12,
    border: `1px solid ${hasError ? COLORS.error : COLORS.border}`,
    background: 'rgba(168, 224, 224, 0.04)',
    color: COLORS.white, fontSize: 15, fontFamily: 'Tajawal, sans-serif',
    outline: 'none', boxSizing: 'border-box' as const,
  }),
  inputError: {
    color: COLORS.error, fontSize: 12, fontFamily: 'Tajawal, sans-serif', marginTop: 6,
  },
  errorBox: {
    padding: '12px 16px', borderRadius: 12,
    border: `1px solid ${COLORS.error}`, background: 'rgba(255, 107, 107, 0.08)', marginBottom: 20,
  },
  errorText: { color: COLORS.error, fontSize: 13, fontFamily: 'Tajawal, sans-serif', margin: 0 },
  button: {
    width: '100%', padding: '14px 24px', borderRadius: 12, border: 'none',
    background: `linear-gradient(135deg, ${COLORS.peach}, #e8b88a)`,
    color: '#0E4A4A', fontSize: 16, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, letterSpacing: '0.8px', marginTop: 8,
  },
  buttonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};

interface FormErrors {
  email?: string;
  password?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [login, { isLoading, error }] = useLoginMutation();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [touched, setTouched] = useState({ email: false, password: false });

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return t('validation.email.required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return t('validation.email.invalid');
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return t('validation.password.required');
    if (password.length < 8) return t('validation.password.minLength');
    if (password.length > 128) return t('validation.password.maxLength');
    return undefined;
  };

  const validateForm = (): boolean => {
    const ne: FormErrors = {};
    const ee = validateEmail(formData.email);
    if (ee) ne.email = ee;
    const pe = validatePassword(formData.password);
    if (pe) ne.password = pe;
    setErrors(ne);
    return Object.keys(ne).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setTouched({ email: true, password: true });
    if (!validateForm()) return;
    try {
      const result = await login(formData).unwrap();
      if (result.status === 'success' && result.data.user) {
        storeAuthData('', result.data.user);
        navigate('/dashboard');
      }
    } catch (err) {
      const ex = err as { data?: { message?: string }; message?: string };
      setLocalError(ex?.data?.message || ex?.message || t('errors.network'));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => { const n = { ...prev }; delete n[name as keyof FormErrors]; return n; });
    }
    if (touched[name as keyof typeof touched]) {
      if (name === 'email') setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
      else if (name === 'password') setErrors((prev) => ({ ...prev, password: validatePassword(value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (name === 'email') setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    else if (name === 'password') setErrors((prev) => ({ ...prev, password: validatePassword(value) }));
  };

  return (
    <div className="login-page" style={s.page}>
      <style>{`
        .login-page input,
        .login-page input:focus,
        .login-page input:active,
        .login-page input:-webkit-autofill,
        .login-page input:-webkit-autofill:hover,
        .login-page input:-webkit-autofill:focus {
          background-color: rgba(168, 224, 224, 0.04) !important;
          color: #FFFFFF !important;
          -webkit-text-fill-color: #FFFFFF !important;
          caret-color: ${COLORS.aqua} !important;
          border-color: inherit !important;
        }
        .login-input:focus {
          border-color: ${COLORS.borderFocus} !important;
        }
        .login-input::placeholder {
          color: rgba(168, 224, 224, 0.3) !important;
        }
        .login-btn:hover:not(:disabled) {
          filter: brightness(1.05);
        }
      `}</style>

      {/* Error */}
      {(error || localError) && (
        <div style={s.errorBox}>
          <p style={s.errorText}>
            {localError || (error && 'data' in error
              ? (error.data as { message?: string })?.message
              : t('errors.network'))}
          </p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: 420 }}>
        <div style={s.inputGroup}>
          <label htmlFor="email" style={s.label}>الإيميل</label>
          <input
            type="email" id="email" name="email" autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className="login-input"
            style={s.input(!!(errors.email && touched.email))}
            placeholder="أدخل الإيميل"
          />
          {errors.email && touched.email && (
            <p style={s.inputError}>{errors.email}</p>
          )}
        </div>

        <div style={s.inputGroup}>
          <label htmlFor="password" style={s.label}>كلمة السر</label>
          <input
            type="password" id="password" name="password" autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            className="login-input"
            style={s.input(!!(errors.password && touched.password))}
            placeholder="••••••••"
          />
          {errors.password && touched.password && (
            <p style={s.inputError}>{errors.password}</p>
          )}
        </div>

        <button
          type="submit" disabled={isLoading}
          className="login-btn"
          style={{ ...s.button, ...(isLoading ? s.buttonDisabled : {}) }}
        >
          {isLoading ? (
            <><ButtonSpinner /><span>جاري التحميل...</span></>
          ) : (
            'تسجيل الدخول'
          )}
        </button>
      </form>
    </div>
  );
}

